import { ReactNode, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, cn, getMotionProps } from '@dashboard/ui'
import { ThemeTokenControl } from './ThemeTokenControl'

interface ThemeOverrideGroupProps {
  label: ReactNode
  tokens: {
    namespace: string
    array: [string, { value: string; $override?: boolean }][]
  }
  disabled: boolean
  findToken: (token: string) => void
  onChange: (path: string, value: string) => void
}

const entryMotionProps = getMotionProps({
  initial: { paddingTop: 0, paddingBottom: 0, height: 0, opacity: 0 },
  animate: { paddingTop: '0.25rem', paddingBottom: '0.75rem', height: 'auto', opacity: 1 },
  exit: { paddingTop: 0, paddingBottom: 0, height: 0, opacity: 0 },
})

export const ThemeOverrideGroup = ({ label, tokens, disabled, findToken, onChange }: ThemeOverrideGroupProps) => {
  const [open, setOpen] = useState(false)

  const hasOverride = useMemo(() => tokens.array.some(([_, token]) => token.$override), [tokens])

  const onClickLabel = () => {
    setOpen(!open)
  }

  const onChangeToken = (path: string, value: string) => {
    if (disabled) {
      return
    }
    onChange([tokens.namespace, path].join('.'), value)
  }

  return (
    <div className="-mx-1 overflow-hidden border-b border-border-secondary p-1 last:border-b-0">
      <div
        className={cn(
          'group/label flex cursor-pointer select-none items-center justify-between py-2 text-caption-md text-text-tertiary transition-colors',
          'hover:text-text-secondary',
          open && 'text-text-secondary',
        )}
        onClick={onClickLabel}
      >
        <div className="flex items-center gap-2 text-caption-md">
          {label} {hasOverride && <div className="size-2 rounded-full bg-fg-brand-primary" />}
        </div>
        <ChevronDown
          className={cn(
            'size-4 text-text-quarterary transition-all',
            'group-hover/label:text-text-secondary',
            open && 'rotate-180 text-text-secondary',
          )}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div {...entryMotionProps} className="flex flex-col gap-3">
            {tokens.array.map(([path, token]) => {
              return (
                <div key={`${tokens.namespace}.${path}`}>
                  <ThemeTokenControl
                    disabled={disabled}
                    token={{ path, value: { value: token.$override ? token.value : '', $override: token.$override } }}
                    findToken={findToken}
                    onChange={value => onChangeToken(path, value)}
                  />
                </div>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
